import { LazyLoader } from '@bp/ui-components';
import { BpPage } from '../../components/BpPage/BpPage';
import { NavigationTabs, NavigationTabsType } from '../../components/NavigationTabs/NavigationTabs';
import { FC, Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches, useParams } from 'react-router-dom';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useQuery } from 'urql';
import { graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';

const GroupPagePageQueryDocument = graphql(`
  query GroupPagePageData($profile: ID!, $groupUuid: ID!) {
    ...PermissionCheckerFields
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      name
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            enabled
            lastSync
          }
        }
      }
      organization {
        uuid
        __typename
      }
    }
  }
`);

export const GroupPage: FC = () => {
  const { t } = useTranslation();
  const { groupUuid } = useParams();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const { cloudLicense, cloudLink } = useContext(OrganizationConfigContext);

  // hack hack hack for overview
  const matches = useMatches();
  const last = [...matches].pop();

  const [{ data }] = useQuery({
    query: GroupPagePageQueryDocument,
    variables: { profile: profileUuid, groupUuid: groupUuid! },
    pause: groupUuid === undefined,
  });
  const perms = usePermissionChecker(data);
  const currentGroup = data?.groups[0];

  const tabs: NavigationTabsType[] = [
    {
      title: t('overview.title'),
      path: `/groups/${groupUuid}`,
      active: last?.id === 'group-overview',
    },
    {
      title: t('workmaterials.title'),
      path: `/groups/${groupUuid}/workmaterials`,
    },
    {
      title: t('appointments.title'),
      path: `/groups/${groupUuid}/appointments`,
    },
    {
      title: t('persons.title'),
      path: `/groups/${groupUuid}/persons`,
    },
    {
      title: 'Cloud', // TODO: external-link Icon
      path: `https://${cloudLink}`,
      target: '_blank',
      disabled:
        !cloudLicense ||
        cloudLink === '' ||
        !currentGroup?.nextcloudConnection?.edges.some((e) => e.properties?.enabled),
    },
  ];

  return (
    <BpPage
      title={currentGroup?.name ?? t('groups.title')}
      isForbidden={
        !currentGroup || !perms?.canViewGroup({ uuid: groupUuid ?? '', organization: currentGroup?.organization })
      }
      organization={pimAuthClaims.getOrganization()}
      hideTitleIfForbidden
    >
      <NavigationTabs className='mb-6' tabs={tabs} />
      <Suspense fallback={<LazyLoader embedded transparent forceHeight='45vh' />}>{<Outlet />}</Suspense>
    </BpPage>
  );
};
